import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';
import { SavedSession } from '@/types/session';
import { CachedAvatar } from '@/shared/components/ui/cached-avatar';

interface AllSessionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessions: SavedSession[];
  onSessionSelect: (session: SavedSession) => void;
  onSessionDelete: (sessionId: string) => void;
}

export const AllSessionsDialog: React.FC<AllSessionsDialogProps> = ({
  open,
  onOpenChange,
  sessions,
  onSessionSelect,
  onSessionDelete,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  if (!open) return null;

  const filteredSessions = sessions.filter(
    (session) =>
      session.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={() => onOpenChange(false)}
    >
      <div
        className="relative rounded-lg shadow-xl w-full max-w-md mx-4"
        style={{
          backgroundColor: '#ffffff',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: '#e5e7eb' }}
        >
          <h2
            className="text-lg font-semibold"
            style={{ color: '#1e293b' }}
          >
            Todas as contas
          </h2>
          <button
            onClick={() => onOpenChange(false)}
            className="p-1 rounded transition-colors"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
              e.currentTarget.style.color = '#1e293b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Search */}
          {sessions.length > 5 && (
            <div
              className="flex items-center border rounded-lg px-3 py-2 h-[38px]"
              style={{
                backgroundColor: '#ffffff',
                borderColor: '#d1d5db',
              }}
            >
              <Icon
                name="search"
                className="w-4 h-4 mr-2"
                style={{ color: '#64748b' }}
              />
              <input
                type="text"
                placeholder="Buscar conta..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 bg-transparent border-0 outline-none text-sm"
                style={{ color: '#1e293b' }}
              />
            </div>
          )}

          {/* Sessions List */}
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {filteredSessions.length > 0 ? (
              filteredSessions.map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-3 rounded-lg transition-colors cursor-pointer"
                  style={{
                    backgroundColor: '#ffffff',
                  }}
                  onClick={() => {
                    onSessionSelect(session);
                    onOpenChange(false);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }}
                >
                  <div className="flex items-center flex-1">
                    {/* Avatar */}
                    <div className="mr-3">
                      <CachedAvatar
                        src={session.avatar}
                        alt={session.name}
                        size="md"
                        isSquircle={true}
                      />
                    </div>

                    {/* User Info */}
                    <div className="flex flex-col overflow-hidden">
                      <span
                        className="text-sm font-medium truncate"
                        style={{ color: '#1e293b' }}
                      >
                        {session.name}
                      </span>
                      <span
                        className="text-xs truncate"
                        style={{ color: '#64748b' }}
                      >
                        {session.email}
                      </span>
                    </div>
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSessionDelete(session.id);
                    }}
                    className="p-2 rounded-md transition-colors ml-2"
                    style={{ color: '#64748b' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                      e.currentTarget.style.color = '#ef4444';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#64748b';
                    }}
                  >
                    <Icon name="trash" className="w-4 h-4" />
                  </button>
                </div>
              ))
            ) : (
              <div
                className="text-center py-8 text-sm"
                style={{ color: '#64748b' }}
              >
                Nenhuma conta encontrada
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
